import type { ReactElement } from "react";
import { ChevronRight, Sparkles } from "lucide-react";
import type { DailyBriefParams } from "@playbookai/widget-schemas";
import { statusToken } from "../../lib/types.js";
import { useWidgetActions } from "../widget-actions.js";
import type { WidgetBodyProps } from "../types.js";

/** User-intent seed handed to the host's PlayerAI rail (NOT an LLM prompt). */
const CONTINUE_PROMPT = "Walk me through this morning's brief";

/**
 * Daily Brief — AI dashboard widget body (data-blind). PlayerAI's morning
 * narrative (headline + narrative) beside a status-toned highlight list. The
 * content rides the resolved view-model (`TView = DailyBriefParams`, mocked
 * today). The "Continue in PlayerAI" affordance is gated on the host-provided
 * `useWidgetActions()` — no provider ⇒ no link; the body never calls AIS itself
 * (ADR-0007 / 0012). Bare content; the `DashboardWidget` adapter supplies the
 * `WidgetCard` frame.
 */
export function DailyBriefWidget({
  data,
}: WidgetBodyProps<DailyBriefParams>): ReactElement {
  const actions = useWidgetActions();
  const highlights = data.highlights ?? [];

  if (!data.headline && !data.narrative && highlights.length === 0) {
    return (
      <p className="text-dense-1 text-pb-text-muted italic">
        No brief yet — PlayerAI will post your morning brief here.
      </p>
    );
  }

  return (
    /* Two columns from the widget's OWN width (PB-5436), not the window:
       41.375rem is the card body at its default full span on a 768px window,
       minus the 1rem scrollbar allowance. The highlights' divider follows
       the same container. */
    <div className="@container/widget">
      <div className="grid grid-cols-1 gap-6 @min-[41.375rem]/widget:grid-cols-[1.4fr_1fr]">
        <div>
          <span className="flex items-center gap-2 mb-3 text-pb-iris-text uppercase tracking-wider text-dense-2 font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            PlayerAI · Daily Brief
          </span>
          {data.headline ? (
            <h2 className="text-lg font-medium text-pb-text leading-snug mb-2">
              {data.headline}
            </h2>
          ) : null}
          {data.narrative ? (
            <p className="text-sm text-pb-text-secondary leading-relaxed">
              {data.narrative}
            </p>
          ) : null}
          {actions?.askPlayerAI ? (
            // eslint-disable-next-line ds/no-hand-built -- kept: an iris text link-button at 14px; Button link ai is either a 36px box or underlined inline type, and no approved look row lists the change.
            <button
              type="button"
              onClick={() => actions.askPlayerAI?.(CONTINUE_PROMPT)}
              className="mt-4 inline-flex items-center gap-1 text-sm text-pb-iris-text hover:underline underline-offset-2 font-medium transition-colors"
            >
              Continue in PlayerAI
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : null}
        </div>
        {highlights.length > 0 ? (
          <div className="@min-[41.375rem]/widget:border-s @min-[41.375rem]/widget:border-pb-border @min-[41.375rem]/widget:ps-6">
            <span className="block text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold mb-2">
              Today&apos;s highlights
            </span>
            <ul className="space-y-2.5">
              {highlights.map((h) => (
                <li
                  key={`${h.status}-${h.text}`}
                  className="flex items-start gap-2"
                >
                  <span
                    className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${statusToken(h.status).dotClass}`}
                  />
                  <span className="text-sm text-pb-text leading-snug">
                    {h.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
